import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Creatable, Switch } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { GraphicArea } from '@/common/components/GraphicArea'
import { JsonInput } from '@/common/components/JsonInput'
import { formatDate } from '@/common/utils/formatDate'
import { useActiveGeneralSettings } from '../../../../common/hooks/useActiveGeneralSettings'
import { useEditGeneralSettings } from './hooks/useEditGeneralSettings'
import {
  EditGeneralSettingsSchema,
  EditGeneralSettingsSchemaForm,
} from './schemas/EditGeneralSettingsSchema'

export default function General() {
  const [isEditing, setIsEditing] = useState(false)

  const { settings, isLoadingSettings } = useActiveGeneralSettings()
  const { t } = useTranslation(['settings', 'common'])
  const hookForm = useForm<EditGeneralSettingsSchemaForm>({
    resolver: zodResolver(EditGeneralSettingsSchema()),
  })
  const { handleEditGeneralSettings, isEditingGeneralSettings } =
    useEditGeneralSettings()

  const handleSubmit = async (data: EditGeneralSettingsSchemaForm) => {
    await handleEditGeneralSettings(data)
    setIsEditing(false)
  }

  useEffect(() => {
    if (!settings || isLoadingSettings) return

    hookForm.reset({
      friendlyUrlOrigin: settings.friendlyUrlOrigin.map((url) => ({
        label: url,
        value: url,
      })),
      singleSignOn: settings.singleSignOn,
      toolsMenu: settings.toolsMenu,
      quickAccess: settings.quickAccess,
      features: JSON.stringify(settings.features, null, 2),
      enableAccountEntity: settings.enableAccountEntity,
    })
  }, [settings, isLoadingSettings, hookForm])

  return (
    <>
      <HeadProvider>
        <Title>{t('general.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <GraphicArea.Header>
          <h3 className="text-2xl font-semibold text-brand-800">
            {t('general.page.title')}
          </h3>
        </GraphicArea.Header>

        <FormProvider {...hookForm}>
          <GraphicArea.Body>
            <form
              onSubmit={hookForm.handleSubmit(handleSubmit)}
              className="ml-1 space-y-4"
            >
              <div className="flex items-center gap-4 w-full">
                <Switch.Root className="border border-base-300 p-3 rounded-lg flex items-center justify-between gap-2 w-full">
                  <Switch.Label
                    htmlFor="singleSignOn"
                    className="cursor-pointer"
                  >
                    {t('general.fields.sso_enabled.label')}
                  </Switch.Label>

                  <Switch.Trigger disabled={!isEditing} name="singleSignOn" />
                </Switch.Root>

                <Switch.Root className="border border-base-300 p-3 rounded-lg flex items-center justify-between gap-2 w-full">
                  <Switch.Label htmlFor="toolsMenu" className="cursor-pointer">
                    {t('general.fields.tools_menu.label')}
                  </Switch.Label>

                  <Switch.Trigger disabled={!isEditing} name="toolsMenu" />
                </Switch.Root>
              </div>

              <div className="flex items-center gap-4 w-full">
                <Switch.Root className="border border-base-300 p-3 rounded-lg flex items-center justify-between gap-2 w-full">
                  <Switch.Label
                    htmlFor="quickAccess"
                    className="cursor-pointer"
                  >
                    {t('general.fields.quick_access.label')}
                  </Switch.Label>

                  <Switch.Trigger disabled={!isEditing} name="quickAccess" />
                </Switch.Root>

                <Switch.Root className="border border-base-300 p-3 rounded-lg flex items-center justify-between gap-2 w-full">
                  <Switch.Label
                    htmlFor="enableAccountEntity"
                    className="cursor-pointer"
                  >
                    {t('general.fields.enable_account_entity.label')}
                  </Switch.Label>

                  <Switch.Trigger
                    disabled={!isEditing}
                    name="enableAccountEntity"
                  />
                </Switch.Root>
              </div>

              <Creatable.Sync
                isDisabled={!isEditing}
                icon={null}
                name="friendlyUrlOrigin"
                isMulti
                noOptionsMessage={() => null}
                label={t('general.fields.friendly_url_origin.label')}
                placeholder={t(
                  'general.fields.friendly_url_origin.placeholder',
                )}
              />

              <JsonInput
                disabled={!isEditing}
                name="features"
                label={t('general.fields.features.label')}
                placeholder={t('general.fields.features.placeholder')}
              />
            </form>
          </GraphicArea.Body>

          <GraphicArea.Footer className="flex flex-row items-end justify-between">
            {settings?.updatedAt && (
              <span className="text-sm text-base-500">
                {t('general.page.updated_at', {
                  date: formatDate(new Date(settings.updatedAt), 'PP'),
                })}
              </span>
            )}

            {isEditing && (
              <div className="flex gap-2">
                <Button.Root
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(!isEditing)
                    hookForm.reset()
                  }}
                >
                  {t('general.actions.cancel_button.text')}
                </Button.Root>

                <Button.Root
                  onClick={hookForm.handleSubmit(handleSubmit)}
                  isLoading={isEditingGeneralSettings}
                >
                  {t('general.actions.save_button.text')}
                </Button.Root>
              </div>
            )}

            {!isEditing && (
              <Button.Root
                type="button"
                onClick={() => setIsEditing(!isEditing)}
              >
                {t('general.actions.edit_button.text')}
              </Button.Root>
            )}
          </GraphicArea.Footer>
        </FormProvider>
      </GraphicArea.Root>
    </>
  )
}
